import React, { useState } from "react";
import { FaRegHeart, FaHeart } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { useFavoritePattern } from "../../../../hooks/useFavoritePattern";
import { useRating } from "../../../../hooks/useRating";
import type { PatternPublicResponse } from '@mrb91002/shared-types';
import { slugify } from "../../../../utils/slugify";
import { LoginModal } from "../../../common/LoginModal";
import { StarRating } from "../../../ui";

import styles from "./PatternCards.module.css";

interface PatternCardsProps {
  patterns: PatternPublicResponse[];
}

export const PatternCards = ({ patterns }: PatternCardsProps) => {
  return (
    <div className={styles.grid}>
      {patterns.map((item) => (
        <PatternCard key={item.pattern_id} pattern={item} />
      ))}
    </div>
  );
};

interface PatternCardProps {
  pattern: PatternPublicResponse;
}

function getDifficultyColor(level: string): string {
  if (!level) return "#444";
  const val = level.toLowerCase();
  if (val.includes("easy") || val.includes("beginner")) return "#1a9763";
  if (val.includes("moderate") || val.includes("intermediate")) return "#F5A623";
  if (val.includes("difficult") && val.includes("very")) return "#de3c3c";
  if (val.includes("difficult")) return "#f56b3c";
  return "#444";
}

const PatternCard = ({ pattern }: PatternCardProps) => {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  const { favorited, loading, showLoginModal, setShowLoginModal, handleFavoriteClick } = useFavoritePattern(pattern.pattern_id);
  
  // Use direct values if available (search results), otherwise calculate from star_counts
  const averageRating = (pattern as any).average_rating ?? (pattern.star_counts ? useRating(pattern.star_counts).averageRating : 0);
  const totalReviews = (pattern as any).review_count ?? (pattern.star_counts ? useRating(pattern.star_counts).totalReviews : 0);

  const goToPattern = (): void => {
    const slug = slugify(pattern.title);
    navigate(`/pattern/${pattern.pattern_id}/${slug}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      goToPattern();
    }
  };

  return (
    <>
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <div
        className={styles.card}
        tabIndex={0}
        onClick={goToPattern}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        role="button"
        aria-label={`View details for ${pattern.title}`}
      >
        <div className={styles.imageWrapper}>
          <img
            className={styles.cardImage}
            src={pattern.images?.[0] || '/placeholder-pattern.png'}
            alt={pattern.display_description}
            draggable={false}
          />
          <button
            className={`${styles.heart} ${hovered || favorited ? styles.heartVisible : ""}`}
            aria-label={favorited ? "Remove from favorites" : "Add to favorites"}
            onClick={handleFavoriteClick}
            tabIndex={-1}
            type="button"
            disabled={loading}
          >
            {favorited ? <FaHeart color="#e84c5a" /> : <FaRegHeart color="#666" />}
          </button>
        </div>
      <div className={styles.body}>
        <div className={styles.topContent}>
          <div className={styles.title}>{pattern.title}</div>
          <div className={styles.description}>{pattern.display_description}</div>
        </div>
        <div className={styles.bottomContent}>
          <div className={styles.starsRow}>
            <StarRating
              value={averageRating}
              reviewCount={totalReviews}
              showValue={false}
              size={18}
              color="#ffa500"
              starStyle={{ marginRight: 1 }}
            />
            <span className={styles.reviewCount}>
              ({totalReviews})
            </span>
          </div>
          <div className={styles.meta}>
            <div
              className={styles.difficulty}
              style={{ color: getDifficultyColor(pattern.difficulty_level) }}
            >
              {pattern.difficulty_level}
            </div>
            <div className={styles.author}>
              {pattern.screen_name}
              {pattern.author_reputation !== null && pattern.author_reputation !== undefined && (
                <span className={styles.reputation}> ({pattern.author_reputation})</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PatternCards;
